/**
 * A small, dependency-free JSON syntax highlighter for response bodies.
 *
 * Response bodies are live server data, not something this codebase authored, so the
 * text is HTML-escaped FIRST and the highlighting regex runs on the escaped string —
 * never the other way round. That order is what keeps a string value containing `<` or
 * `&` (a raw address, an HTML fragment a vendor field echoed back) from being parsed as
 * markup once it lands in `dangerouslySetInnerHTML`.
 */
import { FC, useMemo } from 'react';

const escapeHtml = (s: string) =>
	s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const TOKEN =
	/("(?:\\u[0-9a-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

function highlight(raw: string): string {
	const esc = escapeHtml(raw);
	return esc.replace(TOKEN, match => {
		let cls = 'jn';
		if (match.startsWith('&quot;') || match.startsWith('"')) {
			cls = /:\s*$/.test(match) ? 'jk' : 'js';
		} else if (match === 'true' || match === 'false') cls = 'jb';
		else if (match === 'null') cls = 'jz';
		return `<span class="${cls}">${match}</span>`;
	});
}

export const JsonView: FC<{ value: unknown }> = ({ value }) => {
	const html = useMemo(() => {
		const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
		return highlight(text);
	}, [value]);
	// eslint-disable-next-line react/no-danger
	return <pre className="mono resp-json" dangerouslySetInnerHTML={{ __html: html }} />;
};

/** Response text that did not parse as JSON (a PDF/zip acknowledgement, an HTML error
 *  page, plain text) — rendered as an ordinary text node, which React escapes on its
 *  own, so no highlighting/escaping pass is needed here. */
export const RawTextView: FC<{ text: string }> = ({ text }) => <pre className="mono resp-raw">{text}</pre>;
